import { useMemo, useCallback } from "react";
import {
  type ControlState,
  type ControlAriaAttrs,
  controlStateToAria,
} from "../types";

export interface UseControlStateArgs {
  /** An order is currently in flight (submitting). */
  isTrading?: boolean;
  /** The control is blocked (no quote, cap reached, unset field, …). */
  isDisabled?: boolean;
  /**
   * Number of active/pending trades. When it exceeds {@link activeLimit} the
   * control is treated as disabled (spam guard on lot inputs / steppers).
   */
  activeCount?: number;
  /** Threshold at which `activeCount` forces the disabled state. Default 5. */
  activeLimit?: number;
}

export interface UseControlStateResult {
  /** Resolved control state. */
  state: ControlState;
  /** ARIA attributes to spread onto the control. */
  ariaAttrs: ControlAriaAttrs;
  /** State class ("" | "is-flight" | "is-disabled") for the shared CSS layer. */
  className: string;
  /** True while in-flight. */
  isInFlight: boolean;
  /** True while disabled. */
  isDisabled: boolean;
  /**
   * Click guard — wrap an onClick handler so events are swallowed while the
   * control is in-flight or disabled. Prevents accidental double-fires.
   */
  guard: <E extends { preventDefault: () => void; stopPropagation: () => void }>(
    handler?: (e: E) => void,
  ) => (e: E) => void;
}

/** State-class map. `idle` / `hover` carry no extra class. */
const STATE_CLASS: Record<ControlState, string> = {
  idle: "",
  hover: "",
  inFlight: "is-flight",
  disabled: "is-disabled",
};

/**
 * Derive a shared control state from the raw trading props.
 *
 * Precedence: disabled > inFlight > idle. A disabled control never shows a
 * spinner; an in-flight control is implicitly non-interactive via the guard.
 */
export function resolveControlState({
  isTrading = false,
  isDisabled = false,
  activeCount = 0,
  activeLimit = 5,
}: UseControlStateArgs): ControlState {
  if (isDisabled || activeCount > activeLimit) return "disabled";
  if (isTrading) return "inFlight";
  return "idle";
}

export function useControlState(
  args: UseControlStateArgs,
): UseControlStateResult {
  const { isTrading, isDisabled, activeCount, activeLimit } = args;

  const state = useMemo(
    () => resolveControlState({ isTrading, isDisabled, activeCount, activeLimit }),
    [isTrading, isDisabled, activeCount, activeLimit],
  );

  const isInFlight = state === "inFlight";
  const isDisabledState = state === "disabled";
  const blocked = isInFlight || isDisabledState;

  const guard = useCallback(
    <E extends { preventDefault: () => void; stopPropagation: () => void }>(
        handler?: (e: E) => void,
      ) =>
      (e: E) => {
        if (blocked) {
          e.preventDefault();
          e.stopPropagation();
          return;
        }
        handler?.(e);
      },
    [blocked],
  );

  return {
    state,
    ariaAttrs: controlStateToAria(state),
    className: STATE_CLASS[state],
    isInFlight,
    isDisabled: isDisabledState,
    guard,
  };
}
